import { useState, FormEvent } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Phone, Mail, Calendar, StickyNote, CheckSquare, AlertTriangle, type LucideIcon } from 'lucide-react'
import { api } from '../lib/api'
import AdminLayout from '../components/AdminLayout'
import {
  Field,
  FieldGroup,
  FieldLabel,
} from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Button } from '@/components/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { SearchableSelect } from '@/components/SearchableSelect'

const NONE = '__none__'

type Lead = { id: number; full_name: string }
type Contact = { id: number; full_name: string }
type Account = { id: number; name: string }
type Opportunity = { id: number; name: string }

type Activity = {
  id: number
  activity_type: 'CALL' | 'EMAIL' | 'MEETING' | 'NOTE' | 'TASK'
  subject: string
  description: string
  related_lead: number | null
  related_lead_name: string | null
  related_contact: number | null
  related_contact_name: string | null
  related_account: number | null
  related_account_name: string | null
  related_opportunity: number | null
  related_opportunity_name: string | null
  owner: number | null
  owner_username: string | null
  status: 'PENDING' | 'COMPLETED' | 'CANCELLED'
  due_date: string | null
  completed_at: string | null
  duration_minutes: number | null
  is_overdue: boolean
}

type ActivityInput = Omit<Activity, 'id' | 'related_lead_name' | 'related_contact_name' |
  'related_account_name' | 'related_opportunity_name' | 'owner_username' |
  'completed_at' | 'is_overdue'>

const emptyA: ActivityInput = {
  activity_type: 'TASK', subject: '', description: '',
  related_lead: null, related_contact: null, related_account: null, related_opportunity: null,
  owner: null, status: 'PENDING', due_date: null, duration_minutes: null,
}

const TYPE_ICON: Record<Activity['activity_type'], LucideIcon> = {
  CALL: Phone, EMAIL: Mail, MEETING: Calendar, NOTE: StickyNote, TASK: CheckSquare,
}

function ActivityIcon({ type, className }: { type: Activity['activity_type']; className?: string }) {
  const Icon = TYPE_ICON[type]
  return <Icon className={className || 'h-4 w-4'} />
}

const STATUS_COLOR: Record<Activity['status'], string> = {
  PENDING: 'bg-blue-100 text-blue-800',
  COMPLETED: 'bg-green-100 text-green-800',
  CANCELLED: 'bg-gray-100 text-gray-600',
}

export default function CRMActivities() {
  const qc = useQueryClient()
  const activitiesQ = useQuery({
    queryKey: ['crm', 'activities'],
    queryFn: async () => (await api.get<Activity[]>('/crm/activities/')).data,
  })
  const leadsQ = useQuery({
    queryKey: ['crm', 'leads'],
    queryFn: async () => (await api.get<Lead[]>('/crm/leads/')).data,
  })
  const contactsQ = useQuery({
    queryKey: ['crm', 'contacts'],
    queryFn: async () => (await api.get<Contact[]>('/crm/contacts/')).data,
  })
  const accountsQ = useQuery({
    queryKey: ['crm', 'accounts'],
    queryFn: async () => (await api.get<Account[]>('/crm/accounts/')).data,
  })
  const oppsQ = useQuery({
    queryKey: ['crm', 'opportunities'],
    queryFn: async () => (await api.get<Opportunity[]>('/crm/opportunities/')).data,
  })

  const [editing, setEditing] = useState<Activity | null>(null)
  const [creating, setCreating] = useState(false)
  const [filterType, setFilterType] = useState<string>('ALL')
  const [filterStatus, setFilterStatus] = useState<string>('ALL')

  const createMut = useMutation({
    mutationFn: async (p: ActivityInput) => (await api.post('/crm/activities/', p)).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['crm', 'activities'] }); setCreating(false) },
  })
  const updateMut = useMutation({
    mutationFn: async ({ id, payload }: { id: number; payload: ActivityInput }) =>
      (await api.patch(`/crm/activities/${id}/`, payload)).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['crm', 'activities'] }); setEditing(null) },
  })
  const deleteMut = useMutation({
    mutationFn: async (id: number) => api.delete(`/crm/activities/${id}/`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['crm', 'activities'] }),
  })
  const completeMut = useMutation({
    mutationFn: async (id: number) => (await api.post(`/crm/activities/${id}/complete/`)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['crm', 'activities'] }),
  })
  const reopenMut = useMutation({
    mutationFn: async (id: number) => (await api.post(`/crm/activities/${id}/reopen/`)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['crm', 'activities'] }),
  })

  const activities = activitiesQ.data || []
  const filtered = activities
    .filter((a) => filterType === 'ALL' || a.activity_type === filterType)
    .filter((a) => filterStatus === 'ALL' || a.status === filterStatus)

  return (
    <AdminLayout>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-800">Activities</h2>
          <p className="text-sm text-gray-500">
            {activities.length} total · {activities.filter((a) => a.status === 'PENDING').length} pending ·{' '}
            {activities.filter((a) => a.is_overdue).length} overdue
          </p>
        </div>
        <Button onClick={() => setCreating(true)}>+ Log activity</Button>
      </div>

      <div className="mb-4 space-y-2">
        <div className="flex items-center space-x-2 text-sm flex-wrap gap-y-2">
          <span className="text-gray-500">Type:</span>
          {(['ALL', 'CALL', 'EMAIL', 'MEETING', 'NOTE', 'TASK'] as const).map((t) => (
            <button key={t} onClick={() => setFilterType(t)}
              className={`px-3 py-1 rounded inline-flex items-center gap-1.5 ${filterType === t ? 'bg-gray-800 text-white' : 'bg-white text-gray-700 hover:bg-gray-100'}`}>
              {t !== 'ALL' && <ActivityIcon type={t} className="h-3.5 w-3.5" />}
              {t === 'ALL' ? 'All' : t.charAt(0) + t.slice(1).toLowerCase()}
            </button>
          ))}
        </div>
        <div className="flex items-center space-x-2 text-sm">
          <span className="text-gray-500">Status:</span>
          {(['ALL', 'PENDING', 'COMPLETED', 'CANCELLED'] as const).map((s) => (
            <button key={s} onClick={() => setFilterStatus(s)}
              className={`px-3 py-1 rounded ${filterStatus === s ? 'bg-gray-800 text-white' : 'bg-white text-gray-700 hover:bg-gray-100'}`}>
              {s === 'ALL' ? 'All' : s.charAt(0) + s.slice(1).toLowerCase()}
            </button>
          ))}
        </div>
      </div>

      {activitiesQ.isLoading ? <p className="text-gray-500">Loading…</p> : filtered.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-8 text-center text-gray-500">No activities.</div>
      ) : (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <Th>Type</Th>
                <Th>Subject</Th>
                <Th>Related to</Th>
                <Th>Due</Th>
                <Th>Status</Th>
                <Th>Owner</Th>
                <Th>Actions</Th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {filtered.map((a) => (
                <tr key={a.id} className={`hover:bg-gray-50 ${a.is_overdue ? 'bg-red-50/40' : ''}`}>
                  <Td>
                    <span className="inline-flex items-center gap-1.5">
                      <ActivityIcon type={a.activity_type} className="h-4 w-4 text-gray-500" />
                      <span className="text-xs text-gray-600">{a.activity_type}</span>
                    </span>
                  </Td>
                  <Td>
                    <div className="font-medium">{a.subject}</div>
                    {a.description && (
                      <div className="text-xs text-gray-500 truncate max-w-xs">{a.description}</div>
                    )}
                  </Td>
                  <Td className="text-xs space-y-0.5">
                    {a.related_lead_name && <div>Lead: {a.related_lead_name}</div>}
                    {a.related_contact_name && <div>Contact: {a.related_contact_name}</div>}
                    {a.related_account_name && <div>Account: {a.related_account_name}</div>}
                    {a.related_opportunity_name && <div>Opp: {a.related_opportunity_name}</div>}
                    {!a.related_lead_name && !a.related_contact_name && !a.related_account_name && !a.related_opportunity_name && '—'}
                  </Td>
                  <Td className="text-xs">
                    {a.due_date ? new Date(a.due_date).toLocaleString() : '—'}
                    {a.is_overdue && (
                      <div className="text-red-700 font-semibold mt-0.5 inline-flex items-center gap-1">
                        <AlertTriangle className="h-3.5 w-3.5" /> Overdue
                      </div>
                    )}
                  </Td>
                  <Td><span className={`text-xs px-2 py-0.5 rounded ${STATUS_COLOR[a.status]}`}>{a.status}</span></Td>
                  <Td className="text-xs">{a.owner_username || '—'}</Td>
                  <Td>
                    {a.status === 'PENDING' && (
                      <button onClick={() => completeMut.mutate(a.id)}
                        className="text-green-700 hover:underline text-sm mr-3 font-medium">Complete</button>
                    )}
                    {a.status === 'COMPLETED' && (
                      <button onClick={() => reopenMut.mutate(a.id)}
                        className="text-amber-700 hover:underline text-sm mr-3">Reopen</button>
                    )}
                    <button onClick={() => setEditing(a)} className="text-blue-600 hover:underline text-sm mr-3">Edit</button>
                    <button onClick={() => { if (confirm(`Delete activity?`)) deleteMut.mutate(a.id) }}
                      className="text-red-600 hover:underline text-sm">Delete</button>
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {(creating || editing) && (
        <ActivityDialog
          initial={editing ? { ...editing } as ActivityInput : emptyA}
          isEdit={!!editing}
          leads={leadsQ.data || []}
          contacts={contactsQ.data || []}
          accounts={accountsQ.data || []}
          opportunities={oppsQ.data || []}
          submitting={createMut.isPending || updateMut.isPending}
          error={(createMut.error as any) || (updateMut.error as any)}
          onCancel={() => { setCreating(false); setEditing(null) }}
          onSubmit={(p) => {
            if (editing) updateMut.mutate({ id: editing.id, payload: p })
            else createMut.mutate(p)
          }}
        />
      )}
    </AdminLayout>
  )
}

function ActivityDialog({
  initial, isEdit, leads, contacts, accounts, opportunities, onSubmit, onCancel, submitting, error,
}: {
  initial: ActivityInput
  isEdit: boolean
  leads: Lead[]
  contacts: Contact[]
  accounts: Account[]
  opportunities: Opportunity[]
  onSubmit: (p: ActivityInput) => void
  onCancel: () => void
  submitting: boolean
  error: any
}) {
  const [form, setForm] = useState<ActivityInput>(initial)
  const set = <K extends keyof ActivityInput>(k: K, v: ActivityInput[K]) => setForm((f) => ({ ...f, [k]: v }))
  const submit = (e: FormEvent) => { e.preventDefault(); onSubmit(form) }

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-2xl max-w-md w-full p-6 max-h-[90vh] overflow-y-auto">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">{isEdit ? 'Edit activity' : 'Log activity'}</h3>
        <form onSubmit={submit}>
          <FieldGroup>
            <div className="grid grid-cols-2 gap-3">
              <Field>
                <FieldLabel htmlFor="act-type">Type *</FieldLabel>
                <Select
                  value={form.activity_type}
                  onValueChange={(v) => set('activity_type', v as ActivityInput['activity_type'])}
                >
                  <SelectTrigger id="act-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="TASK">Task</SelectItem>
                    <SelectItem value="CALL">Call</SelectItem>
                    <SelectItem value="EMAIL">Email</SelectItem>
                    <SelectItem value="MEETING">Meeting</SelectItem>
                    <SelectItem value="NOTE">Note</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
              <Field>
                <FieldLabel htmlFor="act-status">Status</FieldLabel>
                <Select
                  value={form.status}
                  onValueChange={(v) => set('status', v as ActivityInput['status'])}
                >
                  <SelectTrigger id="act-status"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="PENDING">Pending</SelectItem>
                    <SelectItem value="COMPLETED">Completed</SelectItem>
                    <SelectItem value="CANCELLED">Cancelled</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
            </div>
            <Field>
              <FieldLabel htmlFor="act-subject">Subject *</FieldLabel>
              <Input id="act-subject" type="text" required value={form.subject}
                onChange={(e) => set('subject', e.target.value)} placeholder="Brief title" />
            </Field>
            <Field>
              <FieldLabel htmlFor="act-description">Description / notes</FieldLabel>
              <Textarea id="act-description" value={form.description}
                onChange={(e) => set('description', e.target.value)} rows={3} />
            </Field>

            <div className="grid grid-cols-2 gap-3">
              <Field>
                <FieldLabel htmlFor="act-due">Due / scheduled</FieldLabel>
                <Input id="act-due" type="datetime-local"
                  value={form.due_date ? form.due_date.slice(0, 16) : ''}
                  onChange={(e) => set('due_date', e.target.value ? new Date(e.target.value).toISOString() : null)} />
              </Field>
              <Field>
                <FieldLabel htmlFor="act-duration">Duration (min)</FieldLabel>
                <Input id="act-duration" type="number" min={0} value={form.duration_minutes || ''}
                  onChange={(e) => set('duration_minutes', e.target.value ? Number(e.target.value) : null)}
                  placeholder="e.g. 30" />
              </Field>
            </div>

            <div className="border-t pt-3">
              <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Linked to (any/all)</h4>
              <div className="space-y-2">
                <Field>
                  <FieldLabel htmlFor="act-lead">Lead</FieldLabel>
                  <SearchableSelect
                    id="act-lead"
                    value={form.related_lead ? String(form.related_lead) : NONE}
                    onChange={(v) => set('related_lead', v === NONE ? null : Number(v))}
                    options={[
                      { value: NONE, label: '—' },
                      ...leads.map((l) => ({ value: String(l.id), label: l.full_name })),
                    ]}
                  />
                </Field>
                <Field>
                  <FieldLabel htmlFor="act-contact">Contact</FieldLabel>
                  <SearchableSelect
                    id="act-contact"
                    value={form.related_contact ? String(form.related_contact) : NONE}
                    onChange={(v) => set('related_contact', v === NONE ? null : Number(v))}
                    options={[
                      { value: NONE, label: '—' },
                      ...contacts.map((c) => ({ value: String(c.id), label: c.full_name })),
                    ]}
                  />
                </Field>
                <Field>
                  <FieldLabel htmlFor="act-account">Account</FieldLabel>
                  <SearchableSelect
                    id="act-account"
                    value={form.related_account ? String(form.related_account) : NONE}
                    onChange={(v) => set('related_account', v === NONE ? null : Number(v))}
                    options={[
                      { value: NONE, label: '—' },
                      ...accounts.map((a) => ({ value: String(a.id), label: a.name })),
                    ]}
                  />
                </Field>
                <Field>
                  <FieldLabel htmlFor="act-opp">Opportunity</FieldLabel>
                  <SearchableSelect
                    id="act-opp"
                    value={form.related_opportunity ? String(form.related_opportunity) : NONE}
                    onChange={(v) => set('related_opportunity', v === NONE ? null : Number(v))}
                    options={[
                      { value: NONE, label: '—' },
                      ...opportunities.map((o) => ({ value: String(o.id), label: o.name })),
                    ]}
                  />
                </Field>
              </div>
            </div>

            {error && (
              <div className="text-red-600 text-sm bg-red-50 px-3 py-2 rounded">
                {String(error?.response?.data ? JSON.stringify(error.response.data) : error.message || error)}
              </div>
            )}

            <div className="flex justify-end space-x-2 pt-3">
              <Button type="button" variant="outline" onClick={onCancel}>Cancel</Button>
              <Button type="submit" disabled={submitting}>
                {submitting ? 'Saving…' : 'Save'}
              </Button>
            </div>
          </FieldGroup>
        </form>
      </div>
    </div>
  )
}

function Th({ children, className }: { children: React.ReactNode; className?: string }) {
  return <th className={`px-4 py-2 text-left text-xs font-semibold text-gray-600 uppercase tracking-wide ${className || ''}`}>{children}</th>
}
function Td({ children, className }: { children: React.ReactNode; className?: string }) {
  return <td className={`px-4 py-2 text-sm ${className || ''}`}>{children}</td>
}
